import React, { useState, useEffect } from 'react';
import { api } from '../hooks/useApi';
import { formatCurrency, formatDate } from '../lib/utils';
import { 
  Users, FileText, DollarSign, Calendar, TrendingUp, Clock, ArrowRight, 
  CreditCard, Receipt, BarChart3
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const CHART_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#94A3B8'];

const STATUS_COLORS = {
  draft: '#94A3B8', pending_approval: '#F59E0B', confirmed: '#3B82F6',
  production: '#8B5CF6', invoiced: '#10B981', completed: '#22C55E', cancelled: '#EF4444'
};

const MetricCard = ({ title, value, icon: Icon, color, subValue }) => (
  <div className="bg-[#121212] border border-[#1E293B] rounded-md p-5" data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium tracking-wide uppercase text-[#94A3B8]">{title}</p>
        <p className="mt-2 text-2xl font-bold text-[#F8FAFC]">{value}</p>
        {subValue && <p className="mt-1 text-sm text-[#94A3B8]">{subValue}</p>}
      </div>
      <div className={`p-2.5 rounded-md ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const ChartTooltipContent = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1E293B] border border-[#334155] rounded-md px-3 py-2 text-xs shadow-lg">
      <p className="text-[#94A3B8] mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey || p.name} style={{ color: p.color }} className="font-medium">
          {p.name}: {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

const DashboardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllFillRates, setShowAllFillRates] = useState(false);

  useEffect(() => {
    const load = async () => {
      try { setMetrics(await api.get('/dashboard/metrics')); }
      catch (e) { console.error('Failed to load metrics:', e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
    </div>
  );

  const statusBadge = (status) => {
    const colors = {
      draft: 'bg-slate-900/50 text-slate-400', confirmed: 'bg-blue-900/30 text-blue-400',
      production: 'bg-purple-900/30 text-purple-400', invoiced: 'bg-emerald-900/30 text-emerald-400',
    };
    return colors[status] || colors.draft;
  };

  const statusChartData = (metrics?.bookings_by_status || []).map(d => ({
    ...d, fill: STATUS_COLORS[d.name] || '#94A3B8'
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">Dashboard</h1>
        <p className="text-[#94A3B8] mt-1">Overview of your publishing operations</p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard title="Advertisers" value={metrics?.total_advertisers || 0} icon={Users} color="bg-blue-900/30 text-blue-400" />
        <MetricCard title="Bookings" value={metrics?.total_bookings || 0} icon={FileText} color="bg-purple-900/30 text-purple-400" subValue={`${metrics?.production_bookings || 0} in production`} />
        <MetricCard title="Total Sales" value={formatCurrency(metrics?.total_revenue || 0)} icon={DollarSign} color="bg-emerald-900/30 text-emerald-400" />
        <MetricCard title="Paid" value={formatCurrency(metrics?.paid_invoices || 0)} icon={CreditCard} color="bg-green-900/30 text-green-400" />
        <MetricCard title="Outstanding" value={formatCurrency(metrics?.outstanding_invoices || 0)} icon={Receipt} color="bg-orange-900/30 text-orange-400" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Month */}
        <div className="bg-[#121212] border border-[#1E293B] rounded-md p-4">
          <h2 className="text-base font-semibold text-[#F8FAFC] mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#3B82F6]" /> Sales by Edition
          </h2>
          {(metrics?.sales_by_edition?.length || 0) > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={metrics.sales_by_edition}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="edition" stroke="#94A3B8" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={60} />
                <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-[#0F172A] border border-[#1E293B] rounded-md px-3 py-2 text-xs">
                        <p className="text-[#F8FAFC] font-medium mb-1">{d.edition}</p>
                        <p className="text-[#3B82F6]">Sales: <strong>{formatCurrency(d.revenue || 0)}</strong></p>
                        <p className="text-[#94A3B8]">Bookings: <strong className="text-[#F8FAFC]">{d.bookings || 0}</strong></p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="revenue" name="Sales" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-[#94A3B8] text-sm text-center py-12">No sales data yet</p>}
        </div>

        {/* Bookings by Status */}
        <div className="bg-[#121212] border border-[#1E293B] rounded-md p-4">
          <h2 className="text-base font-semibold text-[#F8FAFC] mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#3B82F6]" /> Bookings by Status
          </h2>
          {statusChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" nameKey="name" label={({ name, value }) => `${name} (${value})`} labelLine={{ stroke: '#475569' }}>
                  {statusChartData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                </Pie>
                <Tooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-[#94A3B8] text-sm text-center py-12">No bookings yet</p>}
        </div>
      </div>

      {/* Revenue by Advertiser */}
      <div className="bg-[#121212] border border-[#1E293B] rounded-md p-4">
        <h2 className="text-base font-semibold text-[#F8FAFC] mb-4 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-[#3B82F6]" /> Sales by Advertiser (Top 10)
        </h2>
        {(metrics?.revenue_by_advertiser?.length || 0) > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(200, (metrics.revenue_by_advertiser.length) * 40)}>
            <BarChart data={metrics.revenue_by_advertiser} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <YAxis dataKey="name" type="category" stroke="#94A3B8" tick={{ fontSize: 11 }} width={140} />
              <Tooltip content={<ChartTooltipContent formatter={(v) => formatCurrency(v)} />} />
              <Bar dataKey="revenue" name="Sales" radius={[0, 4, 4, 0]}>
                {metrics.revenue_by_advertiser.map((entry, i) => <Cell key={entry.name || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="text-[#94A3B8] text-sm text-center py-8">No advertiser sales data</p>}
      </div>

      {/* Edition Fill Rates */}
      {(metrics?.edition_fill_rates?.length || 0) > 0 && (
        <div className="bg-[#121212] border border-[#1E293B] rounded-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-[#F8FAFC] flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#3B82F6]" /> Edition Fill Rates
            </h2>
            <div className="flex items-center gap-4 text-xs text-[#94A3B8]" data-testid="fill-rate-legend">
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#10B981' }} /> Paid</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#3B82F6' }} /> Other</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#A855F7' }} /> PSA</div>
              <button
                onClick={() => setShowAllFillRates(v => !v)}
                className="text-sm text-[#3B82F6] hover:text-[#2563EB] flex items-center gap-1 ml-2"
                data-testid="toggle-fill-rates-view"
              >
                {showAllFillRates ? 'Show less' : 'View all…'}
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {(() => {
              const fr = metrics.edition_fill_rates;
              if (showAllFillRates) return fr;
              // Pick: 2 prior + current + 3 future (relative to today's publish_date proximity)
              const today = new Date().toISOString().slice(0, 10);
              const past = fr.filter(e => (e.publish_date || '') <= today);
              const future = fr.filter(e => (e.publish_date || '') > today);
              const prior = past.slice(-3);  // 2 previous + the current/most-recent past
              return [...prior, ...future.slice(0, 3)];
            })().map(ed => (
              <Link key={ed.id} to={`/flatplan/${ed.id}`} className="block p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md hover:border-[#3B82F6]/40 transition-colors" data-testid={`fill-rate-${ed.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#F8FAFC]">{ed.name}</span>
                    {ed.is_locked && <span className="text-[10px] bg-yellow-900/30 text-yellow-400 px-1.5 py-0.5 rounded">Locked</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#94A3B8]">
                    <span>{ed.pages_with_ads}/{ed.page_count} pages with ads</span>
                    <span className="font-semibold text-[#F8FAFC]">{ed.fill_pct}%</span>
                  </div>
                </div>
                {(() => {
                  const paidPct = Math.max(0, Math.min(ed.paid_pct ?? 0, 100));
                  const psaPct = Math.max(0, Math.min(ed.psa_pct ?? 0, 100 - paidPct));
                  const otherPct = Math.max(0, Math.min((ed.fill_pct ?? 0) - paidPct - psaPct, 100 - paidPct - psaPct));
                  const tooltip =
                    `Paid ${paidPct}% · Other ${otherPct.toFixed(1)}% · PSA ${psaPct}% · Total ${ed.fill_pct}%`;
                  return (
                    <div
                      className="w-full bg-[#1E293B] rounded-full h-2.5 flex overflow-hidden"
                      title={tooltip}
                      data-testid={`fill-rate-bar-${ed.id}`}
                    >
                      {paidPct > 0 && (
                        <div
                          className="h-2.5 transition-all"
                          style={{ width: `${paidPct}%`, backgroundColor: '#10B981' }}
                          data-testid={`fill-rate-paid-${ed.id}`}
                          data-paid-pct={paidPct}
                          title={`Paid: ${paidPct}%`}
                        />
                      )}
                      {otherPct > 0 && (
                        <div
                          className="h-2.5 transition-all"
                          style={{ width: `${otherPct}%`, backgroundColor: '#3B82F6' }}
                          data-testid={`fill-rate-other-${ed.id}`}
                          title={`Other (unpaid / confirmed): ${otherPct.toFixed(1)}%`}
                        />
                      )}
                      {psaPct > 0 && (
                        <div
                          className="h-2.5 transition-all"
                          style={{ width: `${psaPct}%`, backgroundColor: '#A855F7' }}
                          data-testid={`fill-rate-psa-${ed.id}`}
                          data-psa-pct={psaPct}
                          title={`PSA: ${psaPct}%`}
                        />
                      )}
                    </div>
                  );
                })()}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Grid: Editions + Recent Bookings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#121212] border border-[#1E293B] rounded-md">
          <div className="flex items-center justify-between p-4 border-b border-[#1E293B]">
            <h2 className="text-base font-semibold text-[#F8FAFC] flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#3B82F6]" /> Upcoming Editions
            </h2>
            <Link to="/editions" className="text-sm text-[#3B82F6] hover:text-[#2563EB] flex items-center gap-1" data-testid="view-all-editions">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-[#1E293B]">
            {metrics?.upcoming_editions?.length > 0 ? metrics.upcoming_editions.map(edition => (
              <Link key={edition.id} to={`/flatplan/${edition.id}`} className="flex items-center justify-between p-4 hover:bg-[#1A1A1A] transition-colors" data-testid={`edition-${edition.id}`}>
                <div>
                  <p className="font-medium text-[#F8FAFC]">{edition.name}</p>
                  <p className="text-sm text-[#94A3B8]">{edition.page_count} pages</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#F8FAFC]">{formatDate(edition.publish_date)}</p>
                  <p className="text-xs text-[#94A3B8]">{edition.is_locked ? 'Locked' : 'Open'}</p>
                </div>
              </Link>
            )) : (
              <div className="p-8 text-center text-[#94A3B8]">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>No upcoming editions</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-[#121212] border border-[#1E293B] rounded-md">
          <div className="flex items-center justify-between p-4 border-b border-[#1E293B]">
            <h2 className="text-base font-semibold text-[#F8FAFC] flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#3B82F6]" /> Recent Bookings
            </h2>
            <Link to="/bookings" className="text-sm text-[#3B82F6] hover:text-[#2563EB] flex items-center gap-1" data-testid="view-all-bookings">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-[#1E293B]">
            {metrics?.recent_bookings?.length > 0 ? metrics.recent_bookings.slice(0, 5).map(booking => (
              <div key={booking.id} className="flex items-center justify-between p-4" data-testid={`booking-${booking.id}`}>
                <div>
                  <p className="font-medium text-[#F8FAFC]">{booking.advertiser_name}</p>
                  <p className="text-sm text-[#94A3B8]">{booking.product_type} - {booking.edition_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-[#F8FAFC]">{formatCurrency(booking.rate)}</p>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${statusBadge(booking.status)}`}>{booking.status}</span>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center text-[#94A3B8]">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>No bookings yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
